import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";

const baseUrl = config.baseURL;

const T = {
  dark: "#2E4053",
  darkDeep: "#1C2A3D",
  muted: "#34495E",
  border: "#D1D8E0",
  surface: "#F4F6F9",
  white: "#FFFFFF",
  chipText: "#D1D8E0",
  green: "#16a34a",
  greenBg: "#f0fdf4",
  greenBdr: "#86efac",
  red: "#dc2626",
  redBg: "#fef2f2",
  redBdr: "#fecaca",
};

const Field = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  required,
  textarea,
  rows = 4,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: T.muted,
        letterSpacing: 0.2,
      }}
    >
      {label}
      {required && <span style={{ color: T.red, marginLeft: 2 }}>*</span>}
    </label>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        rows={rows}
        style={{ ...inputStyle, resize: "vertical", height: rows * 24 }}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        style={inputStyle}
      />
    )}
  </div>
);

const inputStyle = {
  padding: "11px 14px",
  border: `1.5px solid ${T.border}`,
  borderRadius: 9,
  fontSize: 14,
  color: T.dark,
  outline: "none",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
  background: T.white,
  transition: "border-color .15s",
};

// ── Main ──────────────────────────────────────────────────────────────────────
const ContactUsPage = ({ currentPageData, onNavigateTo }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    message: "",
    number: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState("");

  const handle = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrMsg("");
    try {
      await axios.post(`${baseUrl}/api/contact_us/submit`, form, {
        headers: { "Content-Type": "application/json" },
      });
      setStatus("success");
      setForm({ full_name: "", email: "", message: "", number: "" });
    } catch (err) {
      setErrMsg(
        err?.response?.data?.message ||
          "Unable to send message. Please try again."
      );
      setStatus("error");
    }
  };

  return (
    <div
      style={{
        background: T.surface,
        minHeight: "100vh",
        fontFamily:
          "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      }}
    >
      {/* Hero */}
      <div
        style={{
          background: `linear-gradient(135deg, ${T.dark} 0%, ${T.darkDeep} 100%)`,
          padding: "72px 24px 80px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            color: T.chipText,
            marginBottom: 16,
            textTransform: "uppercase",
          }}
        >
          GET IN TOUCH
        </div>
        <h1
          style={{
            fontSize: "clamp(28px,4vw,46px)",
            fontWeight: 900,
            color: T.white,
            margin: "0 0 16px",
            letterSpacing: -1,
          }}
        >
          Contact Us
        </h1>
        <p
          style={{
            fontSize: 17,
            color: T.chipText,
            opacity: 0.75,
            maxWidth: 480,
            margin: "0 auto",
            lineHeight: 1.7,
          }}
        >
          Have a question about Spade ERP? Want to request a demo? We'll get
          back to you within a few hours.
        </p>
      </div>

      {/* Content */}
      <div
        style={{
          maxWidth: 1060,
          margin: "0 auto",
          padding: "64px 24px",
          display: "grid",
          gridTemplateColumns: "1fr 420px",
          gap: 48,
          alignItems: "start",
        }}
      >
        {/* Left — contact info */}
        <div>
          <h2
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: T.dark,
              margin: "0 0 8px",
              letterSpacing: -0.5,
            }}
          >
            We'd love to hear from you.
          </h2>
          <p
            style={{
              fontSize: 15,
              color: T.muted,
              lineHeight: 1.75,
              margin: "0 0 40px",
            }}
          >
            Whether you need a live demo, pricing information, technical
            support, or just want to learn more — our team is ready to help.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {[
              {
                icon: "📞",
                title: "Phone & WhatsApp",
                lines: ["+20 10 xxxx xxxx", "Available 9am – 6pm (EGY)"],
              },
              {
                icon: "📧",
                title: "Email",
                lines: ["info@spadeplatform.com"],
              },
              {
                icon: "📍",
                title: "Location",
                lines: ["Cairo, Egypt", "Serving Middle East & Africa"],
              },
              {
                icon: "⏱️",
                title: "Response Time",
                lines: [
                  "Within 2 business hours",
                  "Priority support for paid plans",
                ],
              },
            ].map((item) => (
              <div
                key={item.title}
                style={{ display: "flex", gap: 16, alignItems: "flex-start" }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: T.dark,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: T.dark,
                      marginBottom: 4,
                    }}
                  >
                    {item.title}
                  </div>
                  {item.lines.map((l) => (
                    <div
                      key={l}
                      style={{
                        fontSize: 13.5,
                        color: T.muted,
                        lineHeight: 1.6,
                      }}
                    >
                      {l}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Quick links */}
          <div
            style={{
              marginTop: 40,
              padding: "20px 22px",
              background: T.white,
              borderRadius: 14,
              border: `1px solid ${T.border}`,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: T.dark,
                marginBottom: 12,
              }}
            >
              Quick Links
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {[
                { label: "Book a Demo", onClick: () => onNavigateTo?.("demo") },
                {
                  label: "View Pricing",
                  onClick: () => onNavigateTo?.("pricing"),
                },
                {
                  label: "Documentation",
                  onClick: () =>
                    window.open("https://docs.spadeerp.com", "_blank"),
                },
                {
                  label: "Help Center",
                  onClick: () =>
                    window.open("https://help.spadeerp.com", "_blank"),
                },
              ].map((l) => (
                <span
                  key={l.label}
                  onClick={l.onClick}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: T.dark,
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderRadius: 20,
                    padding: "5px 14px",
                    cursor: "pointer",
                    transition: "background .15s, color .15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = T.dark;
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = T.surface;
                    e.currentTarget.style.color = T.dark;
                  }}
                >
                  {l.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div
          style={{
            background: T.white,
            borderRadius: 18,
            border: `1px solid ${T.border}`,
            boxShadow: "0 4px 24px rgba(46,64,83,.08)",
            overflow: "hidden",
          }}
        >
          {/* Form header */}
          <div style={{ background: T.dark, padding: "22px 28px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.white }}>
              Send us a message
            </div>
            <div
              style={{
                fontSize: 13,
                color: T.chipText,
                opacity: 0.7,
                marginTop: 3,
              }}
            >
              We'll reply within 2 business hours
            </div>
          </div>

          <div style={{ padding: "28px 28px 24px" }}>
            {status === "success" ? (
              <div style={{ textAlign: "center", padding: "32px 16px" }}>
                <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
                <h3
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: T.dark,
                    margin: "0 0 10px",
                  }}
                >
                  Message Sent!
                </h3>
                <p
                  style={{
                    fontSize: 14,
                    color: T.muted,
                    lineHeight: 1.65,
                    margin: "0 0 24px",
                  }}
                >
                  Thank you for reaching out. Our team will get back to you
                  within 2 business hours.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  style={{
                    background: T.dark,
                    color: T.white,
                    border: "none",
                    borderRadius: 9,
                    padding: "11px 28px",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                style={{ display: "flex", flexDirection: "column", gap: 18 }}
              >
                <Field
                  label="Full Name"
                  name="full_name"
                  value={form.full_name}
                  onChange={handle}
                  placeholder="Ahmed Mohamed"
                  required
                />
                <Field
                  label="Phone Number"
                  name="number"
                  value={form.number}
                  onChange={handle}
                  placeholder="+20 10 xxxx xxxx"
                  type="tel"
                  required
                />
                <Field
                  label="Email Address"
                  name="email"
                  value={form.email}
                  onChange={handle}
                  placeholder="you@company.com"
                  type="email"
                  required
                />
                <Field
                  label="Message"
                  name="message"
                  value={form.message}
                  onChange={handle}
                  placeholder="Tell us how we can help you…"
                  required
                  textarea
                  rows={5}
                />

                {errMsg && (
                  <div
                    style={{
                      background: T.redBg,
                      border: `1px solid ${T.redBdr}`,
                      borderRadius: 8,
                      padding: "10px 14px",
                      fontSize: 13,
                      color: T.red,
                    }}
                  >
                    {errMsg}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  style={{
                    background: T.dark,
                    color: T.white,
                    border: "none",
                    borderRadius: 10,
                    padding: "14px",
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: status === "loading" ? "wait" : "pointer",
                    opacity: status === "loading" ? 0.65 : 1,
                    marginTop: 4,
                  }}
                >
                  {status === "loading" ? "Sending…" : "Send Message →"}
                </button>
                <p
                  style={{
                    fontSize: 12,
                    color: T.muted,
                    textAlign: "center",
                    margin: 0,
                    opacity: 0.7,
                  }}
                >
                  We respect your privacy. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsPage;
